import { type ReactNode, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sidebar, SidebarToggle } from './sidebar';
import { MobileOverlay } from './mobile-overlay';
import { FloatingBanners } from './floating-banners';
import { TestPassedPopup } from '@/components/test-passed-popup';
import { SanctionAcknowledgePopup } from '@/components/sanction-acknowledge-popup';
import { TransitionOverlay } from '@/components/ui/transition-overlay';
import { PulseDot } from '@/components/ui/pulse-dot';
import { useSidebarStore } from '@/stores/sidebar';
import { useAuthStore } from '@/stores/auth';
import { useTramoConfetti } from '@/hooks/use-tramo-confetti';
import { useUnreadComunicados } from '@/hooks/use-unread-comunicados';
import { cn } from '@/utils/cn';

const MOBILE_MENU_BTN =
  'fixed left-0 top-base z-30 rounded-r-lg border border-l-0 border-border-subtle bg-surface p-2 text-text-tertiary shadow-sm transition-[opacity,colors] duration-150 hover:text-text-primary md:hidden';

function MobileMenuButton(): ReactNode {
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);
  const mobileOpen = useSidebarStore((s) => s.mobileOpen);
  const { count } = useUnreadComunicados();
  return (
    <button
      onClick={() => setMobileOpen(true)}
      className={cn(
        MOBILE_MENU_BTN,
        mobileOpen && 'pointer-events-none opacity-0'
      )}
      aria-label="Menu"
      aria-hidden={mobileOpen}
      tabIndex={mobileOpen ? -1 : 0}
    >
      <Menu size={16} />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5">
          <PulseDot />
        </span>
      )}
    </button>
  );
}

function MainContent({ showMenu }: { showMenu: boolean }): ReactNode {
  return (
    <div className="relative flex min-w-0 flex-1 flex-col">
      {showMenu && <MobileMenuButton />}
      <FloatingBanners>
        <div className="mx-auto max-w-7xl">
          <Outlet />
        </div>
      </FloatingBanners>
    </div>
  );
}

function SidebarPanel(): ReactNode {
  const { mobileOpen } = useSidebarStore();
  return (
    <div
      className={cn(
        'relative fixed inset-y-0 left-0 z-30 transition-transform md:relative md:translate-x-0',
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      )}
    >
      <Sidebar />
      <div className="absolute -right-3 top-0 hidden md:block">
        <SidebarToggle />
      </div>
    </div>
  );
}

function useCloseSidebarOnNav(): void {
  const { pathname } = useLocation();
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);
  useEffect(() => setMobileOpen(false), [pathname, setMobileOpen]);
}

export function RootLayout(): ReactNode {
  useCloseSidebarOnNav();
  useTramoConfetti();
  const { mobileOpen, setMobileOpen } = useSidebarStore();
  const isBanned = useAuthStore((s) => s.user?.account_status) === 'banned';
  const showShell = !isBanned;
  return (
    <div className="flex h-dvh bg-void">
      {showShell && (
        <>
          <MobileOverlay
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
          />
          <SidebarPanel />
        </>
      )}
      <MainContent showMenu={showShell} />
      <TestPassedPopup />
      <SanctionAcknowledgePopup />
      <TransitionOverlay />
    </div>
  );
}
